/**
 * PATH: packages/ui/src/live-toasts/liveToastStore.ts
 * FILE_ID: UI-LIVE-TOASTS-STORE-001
 * PURPOSE: One module store for activity toasts (new notifications, shift
 *          reminders, approvals), so a timer or a poll diff can raise a toast
 *          from outside React and one stack renders them.
 * INVARIANT: an id raises at most one toast per page life. A repeat push is
 *          ignored; patchLiveToast updates the live card in place.
 */
import { useSyncExternalStore } from 'react'

export type LiveToastTone = 'info' | 'success' | 'warning' | 'error'
export type LiveToastIcon = 'bell' | 'calendar' | 'clock' | 'check' | 'alert'

export interface LiveToastAction {
  label: string
  kind?: 'primary' | 'secondary'
  /** Navigate here through the host router, then dismiss. */
  href?: string
  /** Run, then dismiss; a returned patch keeps the card and shows the result. */
  run?: () => Promise<LiveToastPatch | void> | LiveToastPatch | void
}

export interface LiveToastSpec {
  id: string
  title: string
  body?: string
  meta?: string
  tone?: LiveToastTone
  icon?: LiveToastIcon
  actions?: LiveToastAction[]
  /** Milliseconds until it hides; null keeps it until closed. */
  ttlMs?: number | null
  /** False skips the chime for this toast. */
  sound?: boolean
}

export type LiveToastPatch = Partial<Omit<LiveToastSpec, 'id'>>

export interface LiveToast extends LiveToastSpec {
  createdAt: number
  busy?: boolean
}

export const MAX_LIVE_TOASTS = 20
const MAX_SEEN_IDS = 500
const DEFAULT_TTL_MS = 10_000
const EMPTY: readonly LiveToast[] = []

let toasts: readonly LiveToast[] = EMPTY
const seen = new Set<string>()
const listeners = new Set<() => void>()
const pushListeners = new Set<(toast: LiveToast) => void>()

function emit(): void {
  listeners.forEach((listener) => listener())
}

function remember(id: string): void {
  seen.add(id)
  if (seen.size > MAX_SEEN_IDS) {
    const oldest = seen.values().next().value
    if (oldest !== undefined) seen.delete(oldest)
  }
}

/** Pure: how long a toast stays. Toasts with a run action wait for the person. */
export function liveToastTtl(toast: Pick<LiveToastSpec, 'ttlMs' | 'actions'>): number | null {
  if (toast.ttlMs !== undefined) return toast.ttlMs
  return toast.actions?.some((action) => action.run) ? null : DEFAULT_TTL_MS
}

/** Raise a toast. False when the id was already raised or the spec is empty. */
export function pushLiveToast(spec: LiveToastSpec): boolean {
  if (!spec.id || !spec.title?.trim() || seen.has(spec.id)) return false
  remember(spec.id)
  const toast: LiveToast = { ...spec, createdAt: Date.now() }
  toasts = [toast, ...toasts].slice(0, MAX_LIVE_TOASTS)
  emit()
  pushListeners.forEach((listener) => listener(toast))
  return true
}

export function patchLiveToast(id: string, patch: LiveToastPatch & { busy?: boolean }): void {
  if (!toasts.some((toast) => toast.id === id)) return
  toasts = toasts.map((toast) => (toast.id === id ? { ...toast, ...patch } : toast))
  emit()
}

export function dismissLiveToast(id: string): void {
  if (!toasts.some((toast) => toast.id === id)) return
  toasts = toasts.filter((toast) => toast.id !== id)
  emit()
}

export function clearLiveToasts(): void {
  if (toasts.length === 0) return
  toasts = EMPTY
  emit()
}

/** Subscribe to new toasts (the chime). Returns the unsubscribe. */
export function onLiveToastPushed(listener: (toast: LiveToast) => void): () => void {
  pushListeners.add(listener)
  return () => {
    pushListeners.delete(listener)
  }
}

/** Run one action: navigate, or run and show its result, or dismiss. */
export async function runLiveToastAction(
  id: string,
  action: LiveToastAction,
  navigate?: (href: string) => void,
): Promise<void> {
  if (!action.run) {
    if (action.href) navigate?.(action.href)
    dismissLiveToast(id)
    return
  }
  patchLiveToast(id, { busy: true })
  try {
    const result = await action.run()
    if (result) patchLiveToast(id, { ...result, busy: false })
    else dismissLiveToast(id)
  } catch {
    patchLiveToast(id, {
      busy: false,
      tone: 'error',
      icon: 'alert',
      body: 'That did not work. Try again from the page.',
      actions: action.href ? [{ label: 'Open', kind: 'primary', href: action.href }] : [],
      ttlMs: null,
    })
  }
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

export function getLiveToasts(): readonly LiveToast[] {
  return toasts
}

export function useLiveToasts(): readonly LiveToast[] {
  return useSyncExternalStore(subscribe, getLiveToasts, () => EMPTY)
}

/** Tests only: forget every toast and every raised id. */
export function resetLiveToastsForTest(): void {
  toasts = EMPTY
  seen.clear()
  emit()
}
